import {
  ComboboxChevron,
  type ComboboxData,
  MultiSelect,
  type MultiSelectProps
} from "@mantine/core";
import classNames from "classnames";
import type { CommonFormFieldItemProps } from "../FormField";
import { FormFieldLabel } from "../FormFieldLabel";
import { FormFieldRightSection } from "../FormFieldRightSection";
import type { NumericComboboxItem } from "./FormFieldSelect";

interface FormFieldMultiSelectProps
  extends
    CommonFormFieldItemProps,
    Omit<MultiSelectProps, "data" | "value" | "defaultValue" | "onChange"> {
  type: "multiselect";
  data?: ComboboxData | Array<NumericComboboxItem>;
  defaultValue?: Array<string> | Array<number> | null;
  value?: Array<string> | Array<number> | null;
  onChange?: (value: Array<string> | Array<number> | null) => void;
}

function FormFieldMultiSelect({
  helpText,
  highlight,
  isBetaFeature,
  required,
  type,
  warned,
  data,
  value,
  defaultValue,
  onChange,
  ...fieldSpecificProps
}: FormFieldMultiSelectProps) {
  const hasNumberValues =
    Array.isArray(data) &&
    typeof data[0] === "object" &&
    "value" in data[0] &&
    typeof data[0].value === "number";

  const parsedData: MultiSelectProps["data"] = hasNumberValues
    ? (data as Array<{ value: number; label: string }>).map((item) => ({
        value: item.value.toString(),
        label: item.label
      }))
    : (data as ComboboxData);
  const parsedValue = value?.map(String);
  const parsedDefaultValue = defaultValue?.map(String);

  function handleChangeWithParsing(newValue: Array<string>) {
    if (hasNumberValues) {
      const numericValue = newValue.map((v) => Number(v));
      onChange?.(numericValue);
    } else {
      onChange?.(newValue);
    }
  }

  const manualRightSectionWidth = warned
    ? fieldSpecificProps.clearable === false // default is true
      ? 60
      : 76
    : undefined;

  return (
    <MultiSelect
      {...fieldSpecificProps}
      aria-label={
        typeof fieldSpecificProps.label === "string"
          ? fieldSpecificProps.label
          : undefined
      }
      className={classNames(
        "FormFieldMultiSelect",
        fieldSpecificProps.className
      )}
      data={parsedData}
      defaultValue={parsedDefaultValue}
      label={
        fieldSpecificProps.label && (
          <FormFieldLabel
            helpText={helpText}
            highlight={highlight}
            isBetaFeature={isBetaFeature}
            label={fieldSpecificProps.label}
            required={required}
          />
        )
      }
      labelProps={{ w: "100%", ...fieldSpecificProps.labelProps }}
      rightSection={
        (warned || fieldSpecificProps.rightSection) && (
          <FormFieldRightSection
            rightSection={
              <>
                {fieldSpecificProps.rightSection}
                <ComboboxChevron />
              </>
            }
            warned={warned}
          />
        )
      }
      styles={{
        section: {
          width: fieldSpecificProps.rightSectionWidth || manualRightSectionWidth
        }
      }}
      value={parsedValue}
      onChange={handleChangeWithParsing}
    />
  );
}

export { FormFieldMultiSelect, FormFieldMultiSelectProps };
